import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Avatar,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import QuizIcon from '@mui/icons-material/Quiz';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import LeftNavbar from './LeftNav';
import TopNavbar from './Navbar';
import Breadcrumb from './BreadCrump';
import CountUp from 'react-countup';

const Dashboard = () => {
  const token = localStorage.getItem('token');
  const [user, setUser] = useState(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [certificatesCount, setCertificatesCount] = useState(0);
  const [testsCount, setTestsCount] = useState(0);
  const [chaptersCount, setChaptersCount] = useState(0);
  const [courseProgressData, setCourseProgressData] = useState([]);

  // Fetch logged in user (from 5020)
  useEffect(() => {
    const fetchUser = async () => {
      if (!token) return;
      const res = await fetch('http://196.216.9.34:5020/api/users/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setUser(data);
    };
    fetchUser();
  }, [token]);

  // Fetch dashboard data (from 5050)
  useEffect(() => {
    if (!user) return;

    const fetchDashboardData = async () => {
      try {
        // Completed courses
        const completedRes = await fetch(`http://196.216.9.34:5020/api/completed-courses/${user.id}`);
        const completedData = await completedRes.json();
        setCompletedCount(completedData.count || 0);

        // Certificates
        const certRes = await fetch(`http://196.216.9.34:5020/api/certificates/${user.id}`);
        const certData = await certRes.json();
        setCertificatesCount(certData.certificates?.length || 0);

        // Tests
        const testsRes = await fetch(`http://196.216.9.34:5020/api/tests/${user.id}`);
        const testsData = await testsRes.json();
        setTestsCount(testsData.tests?.length || 0);

        // Chapters summary
        const chaptersRes = await fetch(`http://196.216.9.34:5020/api/chapters-summary/${user.id}`);
        const chaptersData = await chaptersRes.json();
        const totalChapters = chaptersData.chaptersSummary?.reduce((acc, c) => acc + c.total_chapters, 0) || 0;
        setChaptersCount(totalChapters);

        // For chart: derive progress per course
        const progressData = chaptersData.chaptersSummary?.map(c => {
          const completed = completedData.completedCourses.find(cc => cc.id === c.course_id);
          return {
            course: c.title,
            progress: completed ? 100 : Math.floor(Math.random() * 80) + 20, // fallback random progress for demo
          };
        }) || [];
        setCourseProgressData(progressData);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, [user]);

  const dashboardStats = [
    {
      label: 'ISO Courses Completed',
      value: completedCount,
      icon: <AssignmentTurnedInIcon />,
      color: '#42a5f5',
    },
    {
      label: 'Certificates Earned',
      value: certificatesCount,
      icon: <EmojiEventsIcon />,
      color: '#66bb6a',
    },
    {
      label: 'Upcoming Assessments',
      value: testsCount,
      icon: <QuizIcon />,
      color: '#ffa726',
    },
    {
      label: 'Training Chapters',
      value: chaptersCount,
      icon: <AccessTimeIcon />,
      color: '#ab47bc',
    },
  ];

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <LeftNavbar />

      <Box component="main" sx={{ flexGrow: 1, ml: { lg: '240px' }, p: 10 }}>
        <TopNavbar />
        <Breadcrumb currentPage="Dashboard" />
        <Typography variant="h4" fontWeight="bold" mb={3} sx={{ color: '#ff6600' }}>
          Welcome Back to Your Dashboard!
        </Typography>

        {/* Animated Stats Cards */}
        <Grid container spacing={3}>
          {dashboardStats.map((stat, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Card
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 2,
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  boxShadow: 2,
                  borderRadius: 2,
                  bgcolor: 'white',
                  '&:hover': {
                    transform: 'translateY(-6px) scale(1.03)',
                    boxShadow: 6,
                  },
                  '&:hover .icon-bounce': {
                    animation: 'bounce 0.6s',
                  },
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: stat.color,
                    mr: 2,
                    transition: 'transform 0.3s ease',
                  }}
                  className="icon-bounce"
                >
                  {stat.icon}
                </Avatar>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {stat.label}
                  </Typography>
                  <Typography variant="h6">
                    <CountUp end={stat.value} duration={1.5} />
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Course Progress Chart */}
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Course Progress Overview
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={courseProgressData}>
                <XAxis dataKey="course" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="progress" fill="#ff6600" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Keyframe for bounce animation */}
        <style>{`
          @keyframes bounce {
            0% { transform: scale(1); }
            30% { transform: scale(1.25); }
            50% { transform: scale(0.95); }
            100% { transform: scale(1); }
          }
        `}</style>
      </Box>
    </Box>
  );
};

export default Dashboard;
